import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { CategoryDto, ListCategoryDto, MutableCategoryDto } from "../models/categories.models";

export const useGetCategoriesQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['categories', accountId],
		queryFn: async () => {
			let res = await api.get<ListCategoryDto[]>('/categories');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
}

export const useGetCategoryByIdQuery = (categoryId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['category', accountId, categoryId],
		queryFn: async () => {
			let res = await api.get<CategoryDto>(`/categories/${categoryId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && categoryId !== "" && categoryId !== "new" && categoryId !== null
	});
}

export const useCreateCategoryMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<CategoryDto>, Error, MutableCategoryDto>({
		mutationFn: (model) => {
			return api.post('/categories', model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['categories', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
		}
	});
}

export const useUpdateCategoryMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<CategoryDto>, Error, MutableCategoryDto>({
		mutationFn: (model) => {
			return api.put(`/categories/${model.CategoryId}`, model);
		},
		onSuccess: (_data, model) => {
			queryClient.invalidateQueries({ queryKey: ['category', accountId, model.CategoryId] });
			queryClient.invalidateQueries({ queryKey: ['categories', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
		}
	});
}

export const useDeleteCategoryMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, CategoryDto>({
		mutationFn: (category) => {
			return api.delete(`/categories/${category.CategoryId}`);
		},
		onSuccess: (_data, category) => {
			queryClient.invalidateQueries({ queryKey: ['category', accountId, category.CategoryId] });
			queryClient.invalidateQueries({ queryKey: ['categories', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
		}
	});
}